import { DataPackageDto, ProjectDto, PublicationDto } from '@/api/ApiClient';
import { IBackend } from '@/api/BackendApi';
import { NodeType } from '@/types/tree/base';

type DataRouteRootType = Extract<NodeType, 'dataPackage' | 'publication' | 'project'>;

export type DataRouteResolution =
  | { type: 'dataPackage'; id: string; data: DataPackageDto }
  | { type: 'publication'; id: string; data: PublicationDto }
  | { type: 'project'; id: string; data: ProjectDto };

const DOI_RE = /^10\.\d{4,9}\/\S+$/i;
const DATA_PACKAGE_DOI_PREFIX_RE = /^10\.2607\//i;
const NUMERIC_RE = /^\d+$/;

const isNotFound = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const status = (error as { status?: number }).status;
  return status === 404;
};

const notFoundError = (): Error & { status: number } => {
  const error = new Error('Not found') as Error & { status: number };
  error.status = 404;
  return error;
};

const isNumeric = (value: string): boolean => NUMERIC_RE.test(value.trim());
const isDoi = (value: string): boolean => DOI_RE.test(value.trim());
const isDataPackageDoi = (value: string): boolean => DATA_PACKAGE_DOI_PREFIX_RE.test(value.trim());

const normalizeRouteKey = (idOrDoi: string): string => idOrDoi.trim();

/** Tree/API id for /data routes: keep non-numeric route keys (prePublishId, DOI); normalize numeric routes to DB id. */
function dataPackageRouteId(routeKey: string, dto: DataPackageDto): string {
  return isNumeric(routeKey) ? String(dto.id) : normalizeRouteKey(routeKey);
}

type ProbeCacheEntry = {
  /** Cached exists results from prior probes (false = checked, not found). */
  exists: Partial<Record<DataRouteRootType, boolean>>;
  /** Set once resolveExistingDataRouteType finishes for this idOrDoi. */
  resolvedType?: DataRouteRootType | null;
  resolveTypeInFlight?: Promise<DataRouteRootType | null>;
  /** Cached full resolution after resolveDataRouteRoot succeeds. */
  resolution?: DataRouteResolution;
  resolveRootInFlight?: Promise<DataRouteResolution>;
  existsInFlight: Partial<Record<DataRouteRootType, Promise<boolean>>>;
};

const probeCache = new Map<string, ProbeCacheEntry>();

function getProbeCacheEntry(idOrDoi: string): ProbeCacheEntry {
  const key = normalizeRouteKey(idOrDoi);
  let entry = probeCache.get(key);
  if (!entry) {
    entry = { exists: {}, existsInFlight: {} };
    probeCache.set(key, entry);
  }
  return entry;
}

/** Clears probe cache (all keys, or one idOrDoi). Intended for tests. */
export function clearDataRouteProbeCache(idOrDoi?: string): void {
  if (idOrDoi === undefined) {
    probeCache.clear();
    return;
  }
  probeCache.delete(normalizeRouteKey(idOrDoi));
}

/** Probe order for /data/{idOrDoi} when the resource type is ambiguous. */
export function getDataRouteProbeOrder(idOrDoi: string): DataRouteRootType[] {
  if (isNumeric(idOrDoi)) {
    return ['dataPackage', 'publication', 'project'];
  }
  if (isDataPackageDoi(idOrDoi)) {
    return ['dataPackage', 'publication', 'project'];
  }
  if (isDoi(idOrDoi)) {
    return ['publication', 'dataPackage', 'project'];
  }
  return ['project', 'dataPackage', 'publication'];
}

async function fetchExistsForType(backend: IBackend, type: DataRouteRootType, idOrDoi: string): Promise<boolean> {
  if (type === 'dataPackage') {
    return (await backend.data.exists(idOrDoi)).exists ?? false;
  }
  if (type === 'publication') {
    return (await backend.publication.exists(idOrDoi)).exists ?? false;
  }
  return (await backend.project.exists(idOrDoi)).exists ?? false;
}

/**
 * Exists check with per-(idOrDoi, type) cache and in-flight deduplication.
 * Reused by auth (withResourceAuth) and tree loading (resolveDataRouteRoot).
 */
async function existsForType(backend: IBackend, type: DataRouteRootType, idOrDoi: string): Promise<boolean> {
  const entry = getProbeCacheEntry(idOrDoi);
  const cached = entry.exists[type];
  if (cached !== undefined) {
    return cached;
  }

  let inFlight = entry.existsInFlight[type];
  if (!inFlight) {
    inFlight = fetchExistsForType(backend, type, idOrDoi)
      .then((exists) => {
        entry.exists[type] = exists;
        return exists;
      })
      .finally(() => {
        delete entry.existsInFlight[type];
      });
    entry.existsInFlight[type] = inFlight;
  }

  return inFlight;
}

/** Skip a second exists call when type resolution already proved the resource exists. */
function assertExistsFromCache(type: DataRouteRootType, idOrDoi: string): void {
  const entry = getProbeCacheEntry(idOrDoi);
  if (entry.exists[type] === false) {
    throw notFoundError();
  }
  if (entry.resolvedType === type || entry.exists[type] === true) {
    entry.exists[type] = true;
    return;
  }
}

async function probeDataPackage(backend: IBackend, idOrDoi: string): Promise<DataRouteResolution> {
  const entry = getProbeCacheEntry(idOrDoi);
  assertExistsFromCache('dataPackage', idOrDoi);
  if (entry.exists.dataPackage !== true) {
    const exists = await existsForType(backend, 'dataPackage', idOrDoi);
    if (!exists) {
      throw notFoundError();
    }
  }
  const data = await backend.data.get(idOrDoi);
  return { type: 'dataPackage', id: dataPackageRouteId(idOrDoi, data), data };
}

async function probePublication(backend: IBackend, idOrDoi: string): Promise<DataRouteResolution> {
  const entry = getProbeCacheEntry(idOrDoi);
  assertExistsFromCache('publication', idOrDoi);
  if (entry.exists.publication !== true) {
    const exists = await existsForType(backend, 'publication', idOrDoi);
    if (!exists) {
      throw notFoundError();
    }
  }
  const data = await backend.publication.getByIdOrDoi(idOrDoi);
  return { type: 'publication', id: String(data.id), data };
}

async function probeProject(backend: IBackend, idOrDoi: string): Promise<DataRouteResolution> {
  const entry = getProbeCacheEntry(idOrDoi);
  assertExistsFromCache('project', idOrDoi);
  if (entry.exists.project !== true) {
    const exists = await existsForType(backend, 'project', idOrDoi);
    if (!exists) {
      throw notFoundError();
    }
  }
  const data = await backend.project.get(idOrDoi);
  return { type: 'project', id: String(data.id ?? idOrDoi), data };
}

async function resolveExistingDataRouteTypeUncached(
  backend: IBackend,
  idOrDoi: string
): Promise<DataRouteRootType | null> {
  const order = getDataRouteProbeOrder(idOrDoi);
  let lastError: unknown;

  for (const type of order) {
    try {
      if (await existsForType(backend, type, idOrDoi)) {
        return type;
      }
    } catch (error) {
      if (!isNotFound(error)) {
        throw error;
      }
      lastError = error;
    }
  }

  // Fallback: keep behavior robust if probe-order logic changes later.
  const allTypes: DataRouteRootType[] = ['dataPackage', 'publication', 'project'];
  for (const type of allTypes) {
    if (order.includes(type)) continue;
    try {
      if (await existsForType(backend, type, idOrDoi)) {
        return type;
      }
    } catch (error) {
      if (!isNotFound(error)) {
        throw error;
      }
      lastError = error;
    }
  }

  if (lastError) {
    throw lastError;
  }
  return null;
}

/**
 * Returns the first matching root type for /data/{idOrDoi} based on existence checks only.
 * Throws on non-404 errors and returns null if no matching resource exists.
 */
export async function resolveExistingDataRouteType(
  backend: IBackend,
  idOrDoi: string
): Promise<DataRouteRootType | null> {
  const entry = getProbeCacheEntry(idOrDoi);
  if (entry.resolvedType !== undefined) {
    return entry.resolvedType;
  }
  if (entry.resolveTypeInFlight) {
    return entry.resolveTypeInFlight;
  }

  entry.resolveTypeInFlight = resolveExistingDataRouteTypeUncached(backend, idOrDoi)
    .then((type) => {
      entry.resolvedType = type;
      if (type !== null) {
        entry.exists[type] = true;
      }
      return type;
    })
    .finally(() => {
      delete entry.resolveTypeInFlight;
    });

  return entry.resolveTypeInFlight;
}

async function resolveDataRouteRootUncached(backend: IBackend, idOrDoi: string): Promise<DataRouteResolution> {
  const type = await resolveExistingDataRouteType(backend, idOrDoi);
  if (type === null) {
    throw new Error(`Could not resolve /data route target "${idOrDoi}"`);
  }
  if (type === 'dataPackage') return probeDataPackage(backend, idOrDoi);
  if (type === 'publication') return probePublication(backend, idOrDoi);
  return probeProject(backend, idOrDoi);
}

export async function resolveDataRouteRoot(backend: IBackend, idOrDoi: string): Promise<DataRouteResolution> {
  const entry = getProbeCacheEntry(idOrDoi);
  if (entry.resolution) {
    return entry.resolution;
  }
  if (entry.resolveRootInFlight) {
    return entry.resolveRootInFlight;
  }

  entry.resolveRootInFlight = resolveDataRouteRootUncached(backend, idOrDoi)
    .then((resolution) => {
      entry.resolution = resolution;
      entry.resolvedType = resolution.type;
      entry.exists[resolution.type] = true;
      return resolution;
    })
    .finally(() => {
      delete entry.resolveRootInFlight;
    });

  return entry.resolveRootInFlight;
}
